import { useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { driver, Driver, Side } from "driver.js";
import "driver.js/dist/driver.css";
import "../css/driver.css";
import { useTourContext, TourSection } from "../context/TourContext";

const tourStepKey = "cadence_tour_step";

// Steps per section, in tour order. The footer shows progress through the
// whole tour ("3 of 22"); counting per section read "1 of 1" on the welcome
// step and restarted at 1 on every page. Keep in sync with the step arrays below.
const SECTION_STEPS: [TourSection, number][] = [
  ["dashboard", 1],
  ["dsps", 4],
  ["roster", 7],
  ["assets", 4],
  ["createPitch", 6],
];
const TOTAL_TOUR_STEPS = SECTION_STEPS.reduce((sum, [, n]) => sum + n, 0);
const sectionOffset = (section: TourSection) => {
  let offset = 0;
  for (const [name, n] of SECTION_STEPS) {
    if (name === section) return offset;
    offset += n;
  }
  return 0;
};

// Half the arrow's size: driver.css draws it with an 8px border.
const ARROW_HALF = 8;
// Keep the arrow off the card's rounded corners.
const ARROW_INSET = 16;

/*
 * driver.js picks the arrow's slot (start / center / end) from where it
 * *expects* the card to land — e.g. "card begins at the target's top" — not
 * where it actually puts it after keeping the card on screen. So whenever the
 * card is nudged the arrow points beside the target (Add Artist, the roster's
 * Artist Name column, Upload Asset). Once the card is placed, aim the arrow at
 * the centre of the highlighted element instead.
 */
const alignTourArrow = () => {
  const popover = document.querySelector<HTMLElement>(".driver-popover");
  const arrow = popover?.querySelector<HTMLElement>(".driver-popover-arrow");
  const target = document.querySelector<HTMLElement>(".driver-active-element");
  if (!popover || !arrow || !target) return;
  if (arrow.classList.contains("driver-popover-arrow-none")) return;

  const card = popover.getBoundingClientRect();
  const el = target.getBoundingClientRect();
  const clamp = (v: number, max: number) =>
    Math.min(Math.max(v, ARROW_INSET), max - ARROW_INSET - ARROW_HALF * 2);

  const onLeftOrRight =
    arrow.classList.contains("driver-popover-arrow-side-left") ||
    arrow.classList.contains("driver-popover-arrow-side-right");

  if (onLeftOrRight) {
    const y = el.top + el.height / 2 - card.top - ARROW_HALF;
    arrow.style.top = `${clamp(y, card.height)}px`;
    arrow.style.bottom = "auto";
    arrow.style.marginTop = "0";
  } else {
    const x = el.left + el.width / 2 - card.left - ARROW_HALF;
    arrow.style.left = `${clamp(x, card.width)}px`;
    arrow.style.right = "auto";
    arrow.style.marginLeft = "0";
  }
};

// driver.js positions the card right after onPopoverRender, and again (in a
// requestAnimationFrame) on every scroll/resize. Two frames lands after both.
const scheduleAlignTourArrow = () =>
  requestAnimationFrame(() => requestAnimationFrame(alignTourArrow));

export const useCadenceTour = (
  userRole: string | undefined,
  currentSection: TourSection = "dashboard",
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const driverRef = useRef<Driver | null>(null);
  const isTourActiveRef = useRef(false);
  // Section whose steps are on screen; read by the footer for overall progress.
  const activeSectionRef = useRef<TourSection>("dashboard");

  // driver.js re-places the card (and resets its arrow) on scroll/resize.
  // alignTourArrow is a no-op when no tour card is on screen.
  useEffect(() => {
    window.addEventListener("resize", scheduleAlignTourArrow);
    window.addEventListener("scroll", scheduleAlignTourArrow, true);
    return () => {
      window.removeEventListener("resize", scheduleAlignTourArrow);
      window.removeEventListener("scroll", scheduleAlignTourArrow, true);
    };
  }, []);

  // Use shared context for state
  const {
    isMinimized,
    setIsMinimized,
    isTourCompleted,
    markTourCompleted,
    getCurrentTourSection,
    setCurrentTourSection,
    minimizeTour: contextMinimizeTour,
    clearTourProgress,
    tourLoading,
  } = useTourContext();

  const handleCloseClick = useCallback(async () => {
    isTourActiveRef.current = false;
    clearTourProgress();
    await markTourCompleted();
    if (driverRef.current) {
      driverRef.current.destroy();
    }
  }, [clearTourProgress, markTourCompleted]);

  // Navigation handlers
  const handleNavigateToDSPs = useCallback(() => {
    isTourActiveRef.current = false;
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    setCurrentTourSection("dsps");
    localStorage.setItem(tourStepKey, "0");
    navigate("/settings?tab=dsps");
  }, [navigate, setCurrentTourSection]);

  const handleNavigateToRoster = useCallback(() => {
    isTourActiveRef.current = false;
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    setCurrentTourSection("roster");
    localStorage.setItem(tourStepKey, "0");
    navigate("/roster");
  }, [navigate, setCurrentTourSection]);

  const handleNavigateToAddArtist = useCallback(() => {
    isTourActiveRef.current = false;
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    localStorage.setItem(tourStepKey, "1");
    navigate("/roster/add-artist");
  }, [navigate]);

  const handleReturnToRoster = useCallback(
    (nextStep: number) => {
      isTourActiveRef.current = false;
      if (driverRef.current) {
        driverRef.current.destroy();
      }
      localStorage.setItem(tourStepKey, nextStep.toString());
      navigate("/roster");
    },
    [navigate],
  );

  const handleNavigateToAssets = useCallback(() => {
    isTourActiveRef.current = false;
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    setCurrentTourSection("assets");
    localStorage.setItem(tourStepKey, "0");
    navigate("/roster");
  }, [navigate, setCurrentTourSection]);

  const handleNavigateToCreatePitch = useCallback(() => {
    isTourActiveRef.current = false;
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    setCurrentTourSection("createPitch");
    localStorage.setItem(tourStepKey, "0");
    navigate("/pitch/create");
  }, [navigate, setCurrentTourSection]);

  const handleNavigateToDashboard = useCallback(async () => {
    isTourActiveRef.current = false;
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    clearTourProgress();
    await markTourCompleted();
    navigate("/");
  }, [navigate, clearTourProgress, markTourCompleted]);

  // Minimize tour with driver cleanup
  const minimizeTour = useCallback(
    (currentStep: number, section: TourSection) => {
      isTourActiveRef.current = false;
      contextMinimizeTour(currentStep, section);
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    },
    [contextMinimizeTour],
  );

  // Create popover header with minimize and close buttons
  const createPopoverHeader = useCallback(
    (stepIndex: number, section: TourSection) => {
      const headerButtons = document.createElement("div");
      headerButtons.className = "tour-header-buttons";

      const minimizeBtn = document.createElement("button");
      minimizeBtn.className = "tour-minimize-btn";
      minimizeBtn.innerHTML = "−";
      minimizeBtn.title = "Minimize tour";
      minimizeBtn.onclick = () => minimizeTour(stepIndex, section);

      const closeBtn = document.createElement("button");
      closeBtn.className = "tour-close-btn";
      closeBtn.innerHTML = "×";
      closeBtn.title = "Close tour";
      closeBtn.onclick = handleCloseClick;

      headerButtons.appendChild(minimizeBtn);
      headerButtons.appendChild(closeBtn);

      return headerButtons;
    },
    [minimizeTour, handleCloseClick],
  );

  // Create popover footer with progress and navigation button
  const createPopoverFooter = useCallback(
    (
      currentStep: number,
      totalSteps: number,
      buttonText: string,
      onButtonClick: () => void,
    ) => {
      const footerContainer = document.createElement("div");
      footerContainer.className = "tour-footer-container";

      const progressText = document.createElement("span");
      progressText.className = "tour-progress-text";
      // currentStep is 1-based within the active section.
      progressText.textContent = `${sectionOffset(activeSectionRef.current) + currentStep} of ${TOTAL_TOUR_STEPS}`;

      const nextBtn = document.createElement("button");
      nextBtn.className = "tour-next-btn";
      nextBtn.textContent = buttonText;
      nextBtn.onclick = onButtonClick;

      footerContainer.appendChild(progressText);
      footerContainer.appendChild(nextBtn);

      // Every step builds its footer while rendering, so this re-aims the
      // arrow for every step once driver.js has placed the card.
      scheduleAlignTourArrow();

      return footerContainer;
    },
    [],
  );

  // Dashboard tour steps (Section 1)
  const startDashboardTour = useCallback(
    (fromStep: number = 0) => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }

      isTourActiveRef.current = true;
      activeSectionRef.current = "dashboard";
      setCurrentTourSection("dashboard");
      setIsMinimized(false);

      driverRef.current = driver({
        showProgress: false,
        animate: true,
        allowClose: false,
        overlayColor: "rgba(0, 0, 0, 0.75)",
        stagePadding: 10,
        popoverClass: "cadence-tour-popover",
        steps: [
          {
            popover: {
              title: "Welcome to Cadence!",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Managing music releases involves countless repetitive tasks. Cadence automates your DSP pitch submissions, roster management, and analytics tracking—freeing your team to focus on strategy and creative work.</p>
                <p class="tour-secondary-text">This guide will walk you through the essential features. Let's get started.</p>
              </div>
            `,
              side: "over" as Side,
              align: "center",
              showButtons: [],
              popoverClass: "cadence-welcome-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(0, "dashboard");
                const footer = createPopoverFooter(
                  1,
                  1,
                  "Next →",
                  handleNavigateToDSPs,
                );
                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
        ],
      });

      driverRef.current.drive(fromStep);
    },
    [
      setCurrentTourSection,
      setIsMinimized,
      createPopoverHeader,
      createPopoverFooter,
      handleNavigateToDSPs,
    ],
  );

  // DSP Settings tour steps (Section 2)
  const startDSPsTour = useCallback(
    (fromStep: number = 0) => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }

      isTourActiveRef.current = true;
      activeSectionRef.current = "dsps";
      setCurrentTourSection("dsps");
      setIsMinimized(false);

      driverRef.current = driver({
        showProgress: false,
        animate: true,
        allowClose: false,
        overlayColor: "rgba(0, 0, 0, 0.75)",
        stagePadding: 10,
        popoverClass: "cadence-tour-popover",
        steps: [
          // Step 1: Point to Spotify Connect button
          {
            element: '[data-tour="spotify-connect-btn"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">First, connect your DSP accounts.</p>
                <p class="tour-secondary-text">Click "Connect" next to each streaming platform and enter your login credentials. This enables Cadence to submit pitches directly to editorial teams on your behalf.</p>
              </div>
            `,
              side: "right",
              align: "center",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(0, "dsps");

                const footer = createPopoverFooter(1, 4, "Next →", () => {
                  // Click the Spotify connect button to open modal
                  const spotifyConnectBtn = document.querySelector(
                    '[data-tour="spotify-connect-btn"]',
                  ) as HTMLButtonElement;
                  if (spotifyConnectBtn) {
                    spotifyConnectBtn.click();
                    setTimeout(() => {
                      if (driverRef.current) {
                        driverRef.current.moveNext();
                      }
                    }, 300);
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 2: Point to Artist Name field in modal
          {
            element: '[data-tour="artist-name-field"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">In the "Artist Name" field:</p>
                <p class="tour-secondary-text">- For accounts tied to 1-5 specific artists: Enter the artist name(s)</p>
                <p class="tour-secondary-text">- For accounts managing your entire roster: Enter "Full Roster"</p>
                <p class="tour-secondary-text" style="margin-top: 8px;">This helps you track which credentials are associated with which artists.</p>
              </div>
            `,
              side: "right",
              align: "center",
              showButtons: [],
              // Extra gap so the card doesn't sit flush on the Connect modal.
              popoverClass: "cadence-tour-popover cadence-tour-popover-gap",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(1, "dsps");

                const footer = createPopoverFooter(2, 4, "Next →", () => {
                  if (driverRef.current) {
                    driverRef.current.moveNext();
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 3: Point to Connect button in modal
          {
            element: '[data-tour="modal-connect-btn"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Click "Connect" to save your credentials.</p>
              </div>
            `,
              side: "bottom",
              align: "center",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(2, "dsps");

                const footer = createPopoverFooter(3, 4, "Next →", () => {
                  // Close the modal first
                  const closeModalBtn = document.querySelector(
                    '[data-tour="modal-close-btn"]',
                  ) as HTMLButtonElement;
                  if (closeModalBtn) {
                    closeModalBtn.click();
                  }
                  setTimeout(() => {
                    if (driverRef.current) {
                      driverRef.current.moveNext();
                    }
                  }, 300);
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 4: Point to DSP credentials list
          {
            element: '[data-tour="dsp-credentials-list"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Successfully connected! Repeat this process for all DSPs you use.</p>
                <p class="tour-secondary-text">Without valid credentials, Cadence cannot deliver pitches to platforms that require authentication (Spotify, Amazon Music, Deezer).</p>
                <p class="tour-secondary-text" style="margin-top: 8px;">Ready to build your artist roster?</p>
              </div>
            `,
              side: "right",
              align: "center",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(3, "dsps");
                const footer = createPopoverFooter(
                  4,
                  4,
                  "Continue →",
                  handleNavigateToRoster,
                );

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
        ],
      });

      driverRef.current.drive(fromStep);
    },
    [
      setCurrentTourSection,
      setIsMinimized,
      createPopoverHeader,
      createPopoverFooter,
      handleNavigateToRoster,
    ],
  );

  // Roster tour steps (Section 3)
  const startRosterTour = useCallback(
    (fromStep: number = 0) => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }

      isTourActiveRef.current = true;
      activeSectionRef.current = "roster";
      setCurrentTourSection("roster");
      setIsMinimized(false);

      driverRef.current = driver({
        showProgress: false,
        animate: true,
        allowClose: false,
        overlayColor: "rgba(0, 0, 0, 0.75)",
        stagePadding: 10,
        popoverClass: "cadence-tour-popover",
        steps: [
          // Step 1: Point to "Add Artist" button
          {
            element: '[data-tour="add-artist-btn"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">You can add artists to your roster individually or in bulk via CSV upload.</p>
                <p class="tour-secondary-text">Let's start with individual artist creation. Click the "Add Artist" button.</p>
              </div>
            `,
              side: "left",
              align: "start",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(0, "roster");

                const footer = createPopoverFooter(1, 7, "Next →", () => {
                  handleNavigateToAddArtist();
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 2: Point to top of "Add Artist" form
          {
            element: '[data-tour="add-artist-form-header"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Artist information entered here auto-populates in your pitch forms, eliminating repetitive data entry.</p>
                <p class="tour-secondary-text">Add details once—use them across all future pitches for this artist. This includes Spotify URI, Apple ID, location data, and social links.</p>
              </div>
            `,
              side: "bottom",
              align: "start",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(1, "roster");

                const footer = createPopoverFooter(2, 7, "Next →", () => {
                  if (driverRef.current) {
                    driverRef.current.moveNext();
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 3: Point to "Add Artist" submit button
          {
            element: '[data-tour="add-artist-submit-btn"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Once complete, click "Add Artist" to save the profile to your roster.</p>
              </div>
            `,
              side: "left",
              align: "end",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(2, "roster");

                const footer = createPopoverFooter(3, 7, "Next →", () => {
                  handleReturnToRoster(3);
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 4: Point to CSV buttons area
          {
            element: '[data-tour="csv-buttons-area"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">For bulk uploads, use the CSV method:</p>
                <p class="tour-secondary-text">Download the template, complete one row per artist, then import the file. This saves significant time when adding multiple artists at once.</p>
              </div>
            `,
              side: "left",
              align: "start",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(3, "roster");

                const footer = createPopoverFooter(4, 7, "Next →", () => {
                  if (driverRef.current) {
                    driverRef.current.moveNext();
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 5: Point to "Download CSV Template" button
          {
            element: '[data-tour="download-csv-btn"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Click "Download CSV Template" to get the import file.</p>
                <p class="tour-secondary-text">Complete the spreadsheet with artist information. Columns marked with * are required—leave them blank and the import will fail for that artist.</p>
              </div>
            `,
              side: "bottom",
              align: "center",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(4, "roster");

                const footer = createPopoverFooter(5, 7, "Next →", () => {
                  if (driverRef.current) {
                    driverRef.current.moveNext();
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 6: Point to "Import CSV" button
          {
            element: '[data-tour="import-csv-btn"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Save your completed file as CSV format.</p>
                <p class="tour-secondary-text">Click "Import CSV," then drag-and-drop your file or browse to upload it.</p>
                <p class="tour-secondary-text">Cadence validates the data and shows you any errors. Review the preview, then click "Import (X) Valid Artists" to complete the upload.</p>
              </div>
            `,
              side: "bottom",
              align: "end",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(5, "roster");

                const footer = createPopoverFooter(6, 7, "Next →", () => {
                  if (driverRef.current) {
                    driverRef.current.moveNext();
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 7: Completion message (general page)
          {
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Roster setup complete! You've just eliminated hours of repetitive data entry across future pitch submissions.</p>
                <p class="tour-secondary-text" style="margin-top: 8px;">Ready to add promotional assets?</p>
              </div>
            `,
              side: "over" as Side,
              align: "center",
              showButtons: [],
              popoverClass: "cadence-welcome-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(6, "roster");

                const footer = createPopoverFooter(
                  7,
                  7,
                  "Continue →",
                  handleNavigateToAssets,
                );

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
        ],
      });

      driverRef.current.drive(fromStep);
    },
    [
      setCurrentTourSection,
      setIsMinimized,
      createPopoverHeader,
      createPopoverFooter,
      handleNavigateToAddArtist,
      handleReturnToRoster,
      handleNavigateToAssets,
    ],
  );

  // Assets tour steps (Section 4)
  const startAssetsTour = useCallback(
    (fromStep: number = 0) => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }

      // Step 0 lives on the roster page and depends on at least one artist
      // existing. If the roster is empty, skip the assets section entirely
      // and continue with the create-pitch tour.
      if (fromStep === 0) {
        const hasArtist =
          document.querySelector('[data-tour="artist-name-link"]') ||
          document.querySelector('a[href*="/roster/artist-details/"]');
        if (!hasArtist) {
          handleNavigateToCreatePitch();
          return;
        }
      }

      isTourActiveRef.current = true;
      activeSectionRef.current = "assets";
      setCurrentTourSection("assets");
      setIsMinimized(false);

      driverRef.current = driver({
        showProgress: false,
        animate: true,
        allowClose: false,
        overlayColor: "rgba(0, 0, 0, 0.75)",
        stagePadding: 10,
        popoverClass: "cadence-tour-popover",
        steps: [
          // Step 1: Point to Artist Name column on Roster page
          {
            element: '[data-tour="artist-name-column"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">To manage an artist's promotional assets, click their name in the roster table to access their profile.</p>
              </div>
            `,
              side: "right",
              align: "start",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(0, "assets");

                const footer = createPopoverFooter(1, 4, "Next →", () => {
                  // Find an artist row to navigate to. Fall back to the first
                  // artist link in the table if the data-tour attribute (only
                  // applied to row index 0) hasn't rendered yet.
                  const firstArtistLink = (document.querySelector(
                    '[data-tour="artist-name-link"]',
                  ) ||
                    document.querySelector(
                      'a[href*="/roster/artist-details/"]',
                    )) as HTMLAnchorElement | null;

                  if (!firstArtistLink) {
                    // Roster is empty — skip the rest of the assets section
                    // and move on to the Create Pitch tour.
                    handleNavigateToCreatePitch();
                    return;
                  }

                  const href = firstArtistLink.getAttribute("href");
                  const artistIdMatch = href?.match(/artist-details\/(\d+)/);
                  if (!artistIdMatch) {
                    return;
                  }

                  isTourActiveRef.current = false;
                  if (driverRef.current) {
                    driverRef.current.destroy();
                  }
                  localStorage.setItem(tourStepKey, "1");
                  navigate(
                    `/roster/artist-details/${artistIdMatch[1]}?tab=details`,
                  );
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 2: Point to tabs bar on artist page
          {
            element: '[data-tour="assets-tab"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Navigate to the "Assets" tab to view and upload promotional materials for this artist.</p>
              </div>
            `,
              side: "bottom",
              align: "center",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(1, "assets");

                const footer = createPopoverFooter(2, 4, "Next →", () => {
                  // Click the Assets tab if not already active
                  const assetsTab = document.querySelector(
                    '[data-tour="assets-tab"]',
                  ) as HTMLButtonElement;
                  // The tab is <SegmentedTabs>, which sets aria-selected. This
                  // also tested for a legacy active-border class the tabs no
                  // longer carry — a check that could only ever be false.
                  const isActive =
                    assetsTab?.getAttribute("aria-selected") === "true";

                  if (assetsTab && !isActive) {
                    assetsTab.click();
                  }

                  // Wait for the Upload Asset button to be available, then advance
                  const waitForElement = (attempts = 0) => {
                    const uploadBtn = document.querySelector(
                      '[data-tour="upload-asset-btn"]',
                    );
                    if (uploadBtn && driverRef.current) {
                      // Use moveTo with explicit step index instead of moveNext
                      driverRef.current.moveTo(2);
                    } else if (attempts < 10) {
                      setTimeout(() => waitForElement(attempts + 1), 100);
                    } else {
                      // Fallback: if button not found after retries, try moveNext anyway
                      if (driverRef.current) {
                        driverRef.current.moveNext();
                      }
                    }
                  };
                  setTimeout(() => waitForElement(), 300);
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 3: Point to "Upload Asset" button on Assets tab
          {
            element: '[data-tour="upload-asset-btn"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Click "Upload Asset" to add promotional materials.</p>
                <p class="tour-secondary-text">You can upload up to 5 files at once via drag-and-drop or file browser.</p>
                <div class="tour-secondary-text">
                  Categorize each file:
                  <ul class="tour-list">
                    <li>Press Photos</li>
                    <li>Cover Art</li>
                    <li>Single Art</li>
                    <li>BTS (Behind the Scenes)</li>
                    <li>Other</li>
                  </ul>
                </div>
                <p class="tour-secondary-text">Rename files as needed, then click "Upload (X) Files" to save them to your library.</p>
              </div>
            `,
              // Left of the button: there's no room below it for a card this tall.
              side: "left",
              align: "center",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(2, "assets");

                const footer = createPopoverFooter(3, 4, "Next →", () => {
                  if (driverRef.current) {
                    driverRef.current.moveNext();
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 4: Completion message (general page)
          {
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">First asset uploaded successfully!</p>
                <p class="tour-secondary-text" style="margin-top: 8px;">Repeat this process for all artists on your roster. When creating pitches, you'll be able to select from these organized assets instead of searching through folders.</p>
                <p class="tour-secondary-text" style="margin-top: 8px;">Ready to create your first pitch?</p>
              </div>
            `,
              side: "over" as Side,
              align: "center",
              showButtons: [],
              popoverClass: "cadence-welcome-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(3, "assets");

                const footer = createPopoverFooter(
                  4,
                  4,
                  "Continue →",
                  handleNavigateToCreatePitch,
                );

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
        ],
      });

      driverRef.current.drive(fromStep);
    },
    [
      setCurrentTourSection,
      setIsMinimized,
      createPopoverHeader,
      createPopoverFooter,
      navigate,
      handleNavigateToCreatePitch,
    ],
  );

  // Handler to navigate back to dashboard for step 5
  const handleNavigateToDashboardForPitch = useCallback(() => {
    isTourActiveRef.current = false;
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    localStorage.setItem(tourStepKey, "4");
    navigate("/");
  }, [navigate]);

  // Create Pitch tour steps (Section 5)
  const startCreatePitchTour = useCallback(
    (fromStep: number = 0) => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }

      isTourActiveRef.current = true;
      activeSectionRef.current = "createPitch";
      setCurrentTourSection("createPitch");
      setIsMinimized(false);

      driverRef.current = driver({
        showProgress: false,
        animate: true,
        allowClose: false,
        overlayColor: "rgba(0, 0, 0, 0.75)",
        stagePadding: 10,
        popoverClass: "cadence-tour-popover",
        steps: [
          // Step 1: Point above "Where to Pitch?" section
          {
            element: '[data-tour="where-to-pitch-section"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">You're ready to submit your first pitch!</p>
                <p class="tour-secondary-text">The "Where to Pitch?" section lets you select target platforms. For DSPs requiring credentials (Spotify, Amazon Music, Deezer), you'll receive a warning if credentials are missing.</p>
                <p class="tour-secondary-text" style="margin-top: 8px;">Select "Email" to receive a formatted pitch summary you can forward to your personal industry contacts—editors, radio programmers, music supervisors, sync teams, and more.</p>
              </div>
            `,
              side: "top",
              align: "center",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(0, "createPitch");

                const footer = createPopoverFooter(1, 6, "Next →", () => {
                  if (driverRef.current) {
                    driverRef.current.moveNext();
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 2: Point to "General Release Info" section header
          {
            element: '[data-tour="general-release-info-section"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">The "General Release Info" section contains universal metadata requested by all DSPs—regardless of which platforms you select.</p>
              </div>
            `,
              side: "right",
              align: "center",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(1, "createPitch");

                const footer = createPopoverFooter(2, 6, "Next →", () => {
                  if (driverRef.current) {
                    driverRef.current.moveNext();
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 3: Point to "DSP-Specific Information" section header
          {
            // The section only renders once a platform is picked; during the
            // tour usually none is, so fall back to the platform picker the
            // copy refers to rather than an un-anchored card mid-screen.
            element: () =>
              document.querySelector('[data-tour="dsp-specific-info-section"]') ??
              (document.querySelector('[data-tour="where-to-pitch-section"]') as Element),
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">The "DSP-Specific Information" section dynamically populates based on your platform selections. Each DSP has unique metadata requirements.</p>
              </div>
            `,
              side: "right",
              align: "center",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(2, "createPitch");

                const footer = createPopoverFooter(3, 6, "Next →", () => {
                  if (driverRef.current) {
                    driverRef.current.moveNext();
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 4: Point to form submission buttons
          {
            element: '[data-tour="pitch-form-buttons"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">When ready, click "Submit Pitch" to deliver to DSPs.</p>
                <p class="tour-secondary-text">If you need to gather more information first, click "Save Draft" to preserve your work and return later.</p>
              </div>
            `,
              side: "top",
              align: "end",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(3, "createPitch");

                const footer = createPopoverFooter(4, 6, "Next →", () => {
                  handleNavigateToDashboardForPitch();
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 5: Point to pitches table on dashboard
          {
            element: '[data-tour="pitches-table"]',
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Your pitch is complete! It now appears on your dashboard.</p>
                <p class="tour-secondary-text" style="margin-top: 8px;"><strong>Draft Status:</strong> Click to reopen the form, add information, and submit when ready.</p>
                <p class="tour-secondary-text" style="margin-top: 8px;"><strong>Submitted Status:</strong> Access the Actions menu (three dots) to review exactly what was delivered to DSPs. You can still make updates—changes will be resubmitted where platforms allow.</p>
              </div>
            `,
              side: "top",
              align: "center",
              showButtons: [],
              popoverClass: "cadence-tour-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(4, "createPitch");

                const footer = createPopoverFooter(5, 6, "Next →", () => {
                  if (driverRef.current) {
                    driverRef.current.moveNext();
                  }
                });

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
          // Step 6: Completion modal
          {
            popover: {
              title: "",
              description: `
              <div class="tour-content">
                <p class="tour-main-text">Onboarding complete! You now know how to:</p>
                <p class="tour-secondary-text">- Connect DSP credentials</p>
                <p class="tour-secondary-text">- Build your artist roster</p>
                <p class="tour-secondary-text">- Upload promotional assets</p>
                <p class="tour-secondary-text">- Create and submit pitches</p>
                <p class="tour-secondary-text" style="margin-top: 8px;">Access this guide anytime via Settings > Support > Product Tour.</p>
                <p class="tour-secondary-text" style="margin-top: 8px;">Welcome to Cadence—let's streamline your workflow.</p>
              </div>
            `,
              side: "over" as Side,
              align: "center",
              showButtons: [],
              popoverClass: "cadence-welcome-popover",
              onPopoverRender: (popover) => {
                const popoverWrapper = popover.wrapper;
                const header = createPopoverHeader(5, "createPitch");
                // Same footer as every other step, so the last card also shows
                // "22 of 22" and keeps its button on the right.
                const footer = createPopoverFooter(
                  6,
                  6,
                  "Close",
                  handleNavigateToDashboard,
                );

                popoverWrapper.insertBefore(header, popoverWrapper.firstChild);
                popoverWrapper.appendChild(footer);
              },
            },
          },
        ],
      });

      driverRef.current.drive(fromStep);
    },
    [
      setCurrentTourSection,
      setIsMinimized,
      createPopoverHeader,
      createPopoverFooter,
      handleNavigateToDashboardForPitch,
      handleNavigateToDashboard,
    ],
  );

  // Start the appropriate tour based on section
  const startTour = useCallback(
    (section?: TourSection, fromStep: number = 0) => {
      const targetSection = section || currentSection;

      switch (targetSection) {
        case "dashboard":
          startDashboardTour(fromStep);
          break;
        case "dsps":
          startDSPsTour(fromStep);
          break;
        case "roster":
          startRosterTour(fromStep);
          break;
        case "assets":
          startAssetsTour(fromStep);
          break;
        case "createPitch":
          startCreatePitchTour(fromStep);
          break;
        default:
          startDashboardTour(fromStep);
      }
    },
    [
      currentSection,
      startDashboardTour,
      startDSPsTour,
      startRosterTour,
      startAssetsTour,
      startCreatePitchTour,
    ],
  );

  const resumeTour = useCallback(() => {
    const savedSection = getCurrentTourSection();
    const savedStep = parseInt(localStorage.getItem(tourStepKey) || "0", 10);

    // Navigate to the correct page based on the saved section and step
    const getNavigationPath = (): string | null => {
      switch (savedSection) {
        case "dashboard":
          return "/";
        case "dsps":
          return "/settings?tab=dsps";
        case "roster":
          return "/roster";
        case "assets":
          // For assets section, step 0 is on roster page, steps 1+ are on artist details page
          if (savedStep === 0) {
            return "/roster";
          }
          // For steps 1+, we need to navigate to an artist details page
          // We'll let the current page stay if already on artist-details
          if (location.pathname.includes("/roster/artist-details")) {
            return null; // Already on correct page type
          }
          return "/roster"; // Fallback to roster page
        case "createPitch":
          // Steps 0-3 are on create pitch page, steps 4-5 are on dashboard
          if (savedStep <= 3) {
            return "/pitch/create";
          } else {
            return "/";
          }
        default:
          return "/";
      }
    };

    const navigationPath = getNavigationPath();

    // If we need to navigate to a different page, do so and let the auto-start effect handle tour initialization
    if (navigationPath && location.pathname + location.search !== navigationPath) {
      // Un-minimize the tour so the auto-start effect will run on the new page
      setIsMinimized(false);
      isTourActiveRef.current = false;
      navigate(navigationPath);
      // The useEffect will automatically start the tour when we arrive at the correct page
    } else {
      // We're already on the correct page, start the tour immediately
      startTour(savedSection, savedStep);
    }
  }, [getCurrentTourSection, startTour, navigate, location, setIsMinimized]);

  // Auto-start tour based on current page/section
  useEffect(() => {
    // Wait for tour status to load from backend before auto-starting
    if (tourLoading || isTourCompleted() || isMinimized || isTourActiveRef.current) return;

    const savedSection = getCurrentTourSection();
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");
    const savedStep = parseInt(localStorage.getItem(tourStepKey) || "0", 10);

    // Dashboard section - auto-start for basic users
    if (
      currentSection === "dashboard" &&
      savedSection === "dashboard" &&
      userRole === "basic"
    ) {
      const timer = setTimeout(() => {
        startDashboardTour();
      }, 1000);
      return () => clearTimeout(timer);
    }

    // DSPs section - auto-start when navigating from dashboard
    if (
      currentSection === "dsps" &&
      tabParam === "dsps" &&
      (savedSection === "dashboard" || savedSection === "dsps")
    ) {
      const timer = setTimeout(() => {
        startDSPsTour();
      }, 500);
      return () => clearTimeout(timer);
    }

    // Roster section - auto-start when navigating from DSPs or continuing roster tour
    if (
      currentSection === "roster" &&
      (savedSection === "dsps" || savedSection === "roster")
    ) {
      const timer = setTimeout(() => {
        startRosterTour(savedStep);
      }, 500);
      return () => clearTimeout(timer);
    }

    // Assets section - auto-start on roster page (step 0) or artist-details page (steps 1-3)
    if (
      currentSection === "roster" &&
      savedSection === "assets" &&
      savedStep === 0
    ) {
      const timer = setTimeout(() => {
        startAssetsTour(0);
      }, 500);
      return () => clearTimeout(timer);
    }

    // Assets section - auto-start on artist-details page
    // Wait for the target element to exist before starting the tour
    if (
      currentSection === "assets" &&
      savedSection === "assets" &&
      savedStep >= 1
    ) {
      const elementSelectors: Record<number, string> = {
        1: '[data-tour="assets-tab"]',
        2: '[data-tour="upload-asset-btn"]',
        3: "body", // completion step doesn't need specific element
      };

      const targetSelector = elementSelectors[savedStep] || "body";

      // Poll for element existence (wait up to 5 seconds)
      let attempts = 0;
      const maxAttempts = 25;
      const pollInterval = 200;

      const checkElement = () => {
        attempts++;
        const element = document.querySelector(targetSelector);

        if (element) {
          startAssetsTour(savedStep);
        } else if (attempts < maxAttempts) {
          setTimeout(checkElement, pollInterval);
        }
      };

      const timer = setTimeout(checkElement, 500);
      return () => clearTimeout(timer);
    }

    // Create Pitch section - auto-start on /pitch/create page
    if (
      currentSection === "createPitch" &&
      savedSection === "createPitch" &&
      savedStep <= 3
    ) {
      const elementSelectors: Record<number, string> = {
        0: '[data-tour="where-to-pitch-section"]',
        1: '[data-tour="general-release-info-section"]',
        // Step 2 may anchor to the platform picker (see its `element`), and the
        // DSP section never appears without a platform picked — wait for the
        // picker, which is always there, or the tour never resumes.
        2: '[data-tour="where-to-pitch-section"]',
        3: '[data-tour="pitch-form-buttons"]',
      };

      const targetSelector = elementSelectors[savedStep] || "body";

      let attempts = 0;
      const maxAttempts = 25;
      const pollInterval = 200;

      const checkElement = () => {
        attempts++;
        const element = document.querySelector(targetSelector);

        if (element) {
          startCreatePitchTour(savedStep);
        } else if (attempts < maxAttempts) {
          setTimeout(checkElement, pollInterval);
        }
      };

      const timer = setTimeout(checkElement, 500);
      return () => clearTimeout(timer);
    }

    // Create Pitch section - steps 4-5 on dashboard page
    if (
      currentSection === "dashboard" &&
      savedSection === "createPitch" &&
      savedStep >= 4
    ) {
      const elementSelectors: Record<number, string> = {
        4: '[data-tour="pitches-table"]',
        5: "body",
      };

      const targetSelector = elementSelectors[savedStep] || "body";

      let attempts = 0;
      const maxAttempts = 25;
      const pollInterval = 200;

      const checkElement = () => {
        attempts++;
        const element = document.querySelector(targetSelector);

        if (element) {
          startCreatePitchTour(savedStep);
        } else if (attempts < maxAttempts) {
          setTimeout(checkElement, pollInterval);
        }
      };

      const timer = setTimeout(checkElement, 500);
      return () => clearTimeout(timer);
    }
  }, [
    userRole,
    currentSection,
    location,
    isTourCompleted,
    isMinimized,
    getCurrentTourSection,
    startDashboardTour,
    startDSPsTour,
    startRosterTour,
    startAssetsTour,
    startCreatePitchTour,
    tourLoading,
  ]);

  useEffect(() => {
    return () => {
      isTourActiveRef.current = false;
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, []);

  const exitTour = useCallback(async () => {
    clearTourProgress();
    await markTourCompleted();
  }, [clearTourProgress, markTourCompleted]);

  return {
    startTour,
    resumeTour,
    exitTour,
    isTourCompleted,
    markTourCompleted,
    isMinimized,
    minimizeTour: (step: number) => minimizeTour(step, currentSection),
    getCurrentTourSection,
  };
};

// Export with backward-compatible name
export const useDashboardTour = (userRole: string | undefined) =>
  useCadenceTour(userRole, "dashboard");
